import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

export default function OurStory() {
  useSEO({
    title: "Our Story — Why We Started The Tandem T Foundation",
    description: "The Tandem T Foundation was born from lived experience. Read why founders Chrissy Thompson and Rohan Taylor built New Beginnings (for women and children after domestic violence) and UNSPOKEN (men's mental health & wellness).",
    path: "/foundation/our-story",
  });

  return (
    <div data-testid="page-our-story">
      <PageHero
        overline="Why Our Work Is Personal"
        title={<>A Foundation <em className="italic">Built From Experience.</em></>}
        subtitle="Our missions weren't chosen from a list. They grew from experiences within our own family."
        testId="our-story-hero"
      />

      <section className="relative py-20 md:py-28 border-b border-white/10 overflow-hidden" data-testid="our-story-body">
        <div className="absolute left-[-4%] top-[8%] w-[38%] max-w-[520px] opacity-[0.05] pointer-events-none select-none hidden md:block" aria-hidden>
          <img src="/brand/tandem-t-logo.png" alt="" className="w-full h-auto" style={{ mixBlendMode: "screen" }} />
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="space-y-8 text-white/75 font-light leading-[1.9] text-lg md:text-xl">
              <p>
                Founder &amp; President <span className="text-white">Chrissy Thompson</span> understands what
                it means to rebuild life with children after domestic violence &mdash; to start over, navigate
                uncertainty, and search for stability while creating a{" "}
                <strong className="font-semibold text-white">new beginning.</strong> Her experience inspired a
                mission to ensure survivors and their children feel seen, supported, and reminded that
                rebuilding does not have to happen alone.
              </p>
              <p>
                Co-Founder &amp; Vice President <span className="text-white">Rohan Taylor</span> understands
                the pressures men can carry as fathers, providers, and protectors &mdash; and how easily those
                struggles can remain <strong className="font-semibold text-white">unspoken.</strong> His
                experiences inspired a mission to create space for men to talk, connect, find support, and
                prioritize their mental and emotional well-being.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-14 border-t border-b border-white/15 py-12 md:py-16 space-y-8">
              <p className="overline text-white/45">Those experiences shaped the heart of Tandem T Foundation.</p>
              <p className="font-serif italic text-white text-2xl md:text-4xl font-light leading-snug">
                No family should have to navigate its <em className="italic">hardest chapter alone.</em>
              </p>
              <p className="font-serif italic text-white text-2xl md:text-4xl font-light leading-snug">
                A difficult chapter should never be <em className="italic">the end</em> of someone&rsquo;s story.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <p className="text-white/65 font-light leading-relaxed mt-10 max-w-2xl text-base md:text-lg">
              Our experiences explain why the Foundation exists. The people we serve remain the focus of
              everything we do.
            </p>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="mt-14 flex flex-wrap items-center gap-4">
              <Link
                to="/foundation/donate"
                data-testid="our-story-donate"
                className="group inline-flex items-center gap-3 overline px-8 py-5 bg-white text-black hover:bg-white/85 transition-colors"
              >
                Be Part of the Next Chapter <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
              <Link
                to="/foundation"
                data-testid="our-story-mission"
                className="group inline-flex items-center gap-3 overline px-8 py-5 border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
              >
                Explore Our Mission <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
